import { PHARMACY_SERVICES, DISTRICTS } from '@/mocks/pharmacies';

export interface FilterState {
  search: string;
  district: string;
  services: string[];
  onlyOpen: boolean;
  only24h: boolean;
  onlyDelivery: boolean;
  onlyDriveThru: boolean;
  sortBy: 'distance' | 'rating' | 'reviews';
}

interface PharmacyFiltersProps {
  filters: FilterState;
  onChange: (f: FilterState) => void;
  totalCount: number;
}

const PharmacyFilters = ({ filters, onChange, totalCount }: PharmacyFiltersProps) => {
  const toggleService = (id: string) => {
    const next = filters.services.includes(id)
      ? filters.services.filter(s => s !== id)
      : [...filters.services, id];
    onChange({ ...filters, services: next });
  };

  const hasActive =
    filters.district !== 'Все районы' ||
    filters.services.length > 0 ||
    filters.onlyOpen ||
    filters.only24h ||
    filters.onlyDelivery ||
    filters.onlyDriveThru;

  return (
    <aside className="w-72 shrink-0">
      <div
        className="sticky top-24 rounded-2xl overflow-hidden"
        style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)' }}
      >
        {/* Header */}
        <div
          className="px-5 py-4 flex items-center justify-between"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,45,120,0.04)' }}
        >
          <div className="flex items-center gap-2">
            <i className="ri-equalizer-3-line" style={{ color: '#ff2d78' }} />
            <span className="font-orbitron font-bold text-xs text-white">Фильтры</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="font-mono text-[10px] text-white/30">{totalCount} аптек</span>
            {hasActive && (
              <button
                onClick={() => onChange({
                  search: filters.search,
                  district: 'Все районы',
                  services: [],
                  onlyOpen: false,
                  only24h: false,
                  onlyDelivery: false,
                  onlyDriveThru: false,
                  sortBy: filters.sortBy,
                })}
                className="font-mono text-[10px] cursor-pointer transition-colors"
                style={{ color: '#ff2d78' }}
              >
                Сбросить
              </button>
            )}
          </div>
        </div>

        <div className="px-5 py-4 space-y-5">
          {/* Quick toggles */}
          <div>
            <p className="font-mono text-[10px] tracking-widest text-white/30 mb-3">БЫСТРЫЕ ФИЛЬТРЫ</p>
            <div className="space-y-2.5">
              {[
                { label: 'Только открытые', key: 'onlyOpen' as const, icon: 'ri-door-open-line', color: '#39ff14' },
                { label: 'Работают 24/7', key: 'only24h' as const, icon: 'ri-moon-line', color: '#00f5ff' },
                { label: 'Есть доставка', key: 'onlyDelivery' as const, icon: 'ri-truck-line', color: '#ff2d78' },
                { label: 'Drive-thru', key: 'onlyDriveThru' as const, icon: 'ri-car-line', color: '#f0ff00' },
              ].map(({ label, key, icon, color }) => (
                <button
                  key={key}
                  onClick={() => onChange({ ...filters, [key]: !filters[key] })}
                  className="w-full flex items-center justify-between cursor-pointer group"
                >
                  <div className="flex items-center gap-2.5">
                    <div
                      className="w-7 h-7 flex items-center justify-center rounded-lg transition-all"
                      style={{ background: filters[key] ? `${color}20` : 'rgba(255,255,255,0.04)', border: `1px solid ${filters[key] ? color + '40' : 'rgba(255,255,255,0.08)'}` }}
                    >
                      <i className={`${icon} text-xs`} style={{ color: filters[key] ? color : 'rgba(255,255,255,0.3)' }} />
                    </div>
                    <span className="font-mono text-xs text-white/55 group-hover:text-white/75 transition-colors">{label}</span>
                  </div>
                  <div
                    className="w-9 h-5 rounded-full transition-all duration-300 relative shrink-0"
                    style={{ background: filters[key] ? color : 'rgba(255,255,255,0.1)' }}
                  >
                    <div
                      className="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all duration-300"
                      style={{ left: filters[key] ? '19px' : '2px' }}
                    />
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* District */}
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '16px' }}>
            <p className="font-mono text-[10px] tracking-widest text-white/30 mb-3">РАЙОН</p>
            <div className="flex flex-wrap gap-1.5">
              {DISTRICTS.map(d => (
                <button
                  key={d}
                  onClick={() => onChange({ ...filters, district: d })}
                  className="whitespace-nowrap px-2.5 py-1 rounded-full font-mono text-[11px] cursor-pointer transition-all duration-200"
                  style={filters.district === d
                    ? { background: 'rgba(255,45,120,0.2)', border: '1px solid rgba(255,45,120,0.5)', color: '#ff2d78' }
                    : { border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.4)' }
                  }
                >
                  {d}
                </button>
              ))}
            </div>
          </div>

          {/* Services */}
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '16px' }}>
            <p className="font-mono text-[10px] tracking-widest text-white/30 mb-3">УСЛУГИ</p>
            <div className="space-y-1.5">
              {PHARMACY_SERVICES.map(svc => {
                const active = filters.services.includes(svc.id);
                return (
                  <button
                    key={svc.id}
                    onClick={() => toggleService(svc.id)}
                    className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl cursor-pointer transition-all duration-200"
                    style={active
                      ? { background: `${svc.color}12`, border: `1px solid ${svc.color}35` }
                      : { border: '1px solid rgba(255,255,255,0.06)', background: 'transparent' }
                    }
                  >
                    <div
                      className="w-6 h-6 flex items-center justify-center rounded-lg shrink-0"
                      style={{ background: active ? `${svc.color}20` : 'rgba(255,255,255,0.05)' }}
                    >
                      <i className={`${svc.icon} text-xs`} style={{ color: active ? svc.color : 'rgba(255,255,255,0.3)' }} />
                    </div>
                    <span className="font-mono text-xs flex-1 text-left" style={{ color: active ? svc.color : 'rgba(255,255,255,0.5)' }}>
                      {svc.label}
                    </span>
                    {active && <i className="ri-check-line text-xs shrink-0" style={{ color: svc.color }} />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Sort */}
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '16px' }}>
            <p className="font-mono text-[10px] tracking-widest text-white/30 mb-3">СОРТИРОВКА</p>
            <div className="flex flex-col gap-1.5">
              {([
                { id: 'distance', label: 'По расстоянию', icon: 'ri-map-pin-line' },
                { id: 'rating', label: 'По рейтингу', icon: 'ri-star-line' },
                { id: 'reviews', label: 'По отзывам', icon: 'ri-chat-3-line' },
              ] as { id: FilterState['sortBy']; label: string; icon: string }[]).map(s => (
                <button
                  key={s.id}
                  onClick={() => onChange({ ...filters, sortBy: s.id })}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl font-mono text-xs cursor-pointer transition-all duration-200"
                  style={filters.sortBy === s.id
                    ? { background: 'rgba(255,45,120,0.12)', border: '1px solid rgba(255,45,120,0.35)', color: '#ff2d78' }
                    : { border: '1px solid rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.4)' }
                  }
                >
                  <i className={s.icon} />
                  {s.label}
                  {filters.sortBy === s.id && <i className="ri-check-line ml-auto" />}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default PharmacyFilters;
